import * as React from 'react';
import styled, { css } from 'styled-components';

const ErrorMessage = styled.span<{ initErrorSpacing?: boolean }>(({ initErrorSpacing, theme }) => ({
  fontWeight: 700,
  fontSize: '10px',
  lineHeight: '15px',
  fontStretch: 'normal',
  fontStyle: 'normal',
  letterSpacing: 'normal',
  textAlign: 'left',
  marginTop: '5px',
  height: `${initErrorSpacing ? '18px' : '22px'}`,
  color: theme.colors.error,
}));

// tslint:disable-next-line:variable-name
const SVerificationCode = styled.div(() => ({
  display: 'flex',
}));

const SVerificationInputWrap = styled.div<{ error?: boolean; isLong?: boolean }>(
  ({ theme, error, isLong }) => css`
    display: flex;
    flex-direction: column;
    border: 1.5px solid ${error ? theme.colors.error : theme.colors.inputBackground};
    border-radius: ${theme.borderRadiusCommon}px;
    background-color: ${theme.colors.inputBackground};
    height: 60px;
    width: 52px;

    &:not(:last-child) {
      margin-right: ${isLong ? theme.spacing.smallMedium : theme.spacing.large}px; // spacing is being overridden by new card apply spacing
    }

    @media only screen and ${theme.device.max.mobileL} {
      width: 50px;
    }
  `
);

const SVerificationInput = styled.input(
  ({ theme }) => `
    border-width: 0;
    background-color: transparent;
    flex-grow: 1;
    text-align: center;
    font-size: ${theme.font.size.medium};
    font-weight: ${theme.font.weight.bold};
    color: ${theme.colors.primaryBright};
`
);

export interface IVerificationCodeProps {
  onFilled?: (verificationCode: string) => void;
  value?: string;
  error?: boolean;
  length?: number;
  disabled?: boolean;
  autoFocus?: boolean;
}

type VerificationCodeState = {
  values: string[];
  pinLength: number;
};

export class VerificationCode extends React.Component<IVerificationCodeProps, VerificationCodeState> {
  private readonly DEFAULT_PIN_CODE_LENGTH = 4;

  private refsMap: Array<React.RefObject<HTMLInputElement>> = [];

  public state = {
    values: [''],
    pinLength: 0,
  };

  constructor(props: IVerificationCodeProps) {
    super(props);
    const pinLength = props.length || this.DEFAULT_PIN_CODE_LENGTH;

    for (let i = 0; i < pinLength; i++) {
      this.refsMap.push(React.createRef<HTMLInputElement>());
    }
  }

  public componentDidMount() {
    const pinLength = this.props.length || this.DEFAULT_PIN_CODE_LENGTH;
    const values: string[] = [];
    let focus = 0;

    for (let i = 0; i < pinLength; i++) {
      values.push('');
    }
    const newState = {
      ...this.state,
      values,
      pinLength,
    };
    const receivedValue = this.props.value;
    if (receivedValue && receivedValue.match(/^[0-9]+$/)) {
      newState.values = receivedValue.split('');
      focus = pinLength - 1;
    }

    this.setState(newState, () => {
      if (this.props.autoFocus) {
        const inputRef = this.refsMap[focus];
        if (inputRef && inputRef.current && inputRef.current.focus) {
          inputRef.current.focus();
        }
      }

      // const val = this.state.values.join('');
      // if (this.props.onFilled) {
      //   this.props.onFilled(val);
      // }
    });
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps.error !== prevState.error) {
      return { error: nextProps.error };
    } else return null;
  }

  /**
   * Gives the ability to move back when backspacing
   */
  public handleKeyDown = (i: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    const { values } = this.state;
    if (event.key === 'Backspace' && values[i] === '' && i > 0) {
      const inputRef = this.refsMap[i - 1];
      if (inputRef && inputRef.current && inputRef.current.focus) {
        inputRef.current.focus();
      }
    }
  };

  public handleOnChange = async (i: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const { pinLength, values } = this.state;
    const { onFilled } = this.props;
    const eventValue = event.target.value;

    const updateState = async (newValues: string[]) => {
      this.setState({ ...this.state, values: newValues }, async () => {
        const val = newValues.join('');
        if (val.length === pinLength && onFilled) {
          const val = this.state.values.join('');
          onFilled(val);

          this.setState({ ...this.state, values: new Array(this.state.pinLength).fill('') });
          const inputRef = this.refsMap[0];
          inputRef.current?.focus?.();
        }
      });
    };

    if (eventValue.length > 2) {
      // see if we can use the string to fill out our values
      if (eventValue.length === values.length && eventValue.match(/^[0-9]+$/)) {
        updateState(eventValue.split(''));
      }
      return;
    }

    if (eventValue === '') {
      const newValues = values.slice();
      newValues[i] = '';
      updateState(newValues);
      return;
    }

    // we want to override the input value with the last digit typed
    const currentValue = values[i];
    let newValue = eventValue;

    if (currentValue[0] === eventValue[0]) {
      newValue = eventValue[1];
    } else if (currentValue[0] === eventValue[1]) {
      newValue = eventValue[0];
    }

    if (newValue.match(/^[0-9]$/)) {
      const newValues = values.slice();
      newValues[i] = newValue;
      updateState(newValues);
      if (i < pinLength - 1) {
        const inputRef = this.refsMap[i + 1];
        if (inputRef && inputRef.current && inputRef.current.focus) {
          inputRef.current.focus();
        }
      }
    }
  };

  public render() {
    const { length, disabled } = this.props;
    return (
      <>
        <SVerificationCode data-at="verificationCode">
          {this.state.values.map((_v, i) => (
            <SVerificationInputWrap
              error={this.props.error || false}
              key={i}
              // isLong={length > this.DEFAULT_PIN_CODE_LENGTH}  //TODO: return after backend fix issue SBT-8569
            >
              <SVerificationInput
                type="text"
                inputMode="numeric"
                pattern="\d*"
                name={`number${i + 1}`}
                data-at={`number${i + 1}`}
                data-testid={`verification-number-${i + 1}`}
                data-neuro-label={`verification--number-${i + 1}`}
                value={this.state.values[i]}
                ref={this.refsMap[i]}
                onChange={this.handleOnChange.bind(this, i)}
                onKeyDown={this.handleKeyDown.bind(this, i)}
                autoComplete="one-time-code"
                disabled={disabled}
              />
            </SVerificationInputWrap>
          ))}
        </SVerificationCode>
        {this.props.error && (
          <ErrorMessage
            {...{
              className: 'error-message',
              initErrorSpacing: false,
              'data-at': 'pincode-error',
              'data-testid': 'error-message',
              tabIndex: 1,
            }}
          >
            Incorrect code
          </ErrorMessage>
        )}
      </>
    );
  }
}
